import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Button, Input } from "../../components/ui";
import { APP_ENV, type AppEnv } from "../../config/app";
import { coreConfig } from "../../config/core";
import { useAuth } from "../../hooks";
import { validateEmailOrUsername, validatePasswordMinLength, homePath, accountTypeFromRoles, rolesFromClaims } from "../../utils";
import { authStorage } from "../../services/authStorage";
import { startHostedOnboarding } from "../../services/hostedOnboarding";

interface LoginLocationState {
    onboardingError?: string;
}

const ENVIRONMENT_LABELS: Record<Exclude<AppEnv, 'prod'>, string> = {
    local: 'Development',
    stage: 'Staging',
};

function environmentLabel(env: AppEnv): string | null {
    if (env === 'prod') return null;
    return ENVIRONMENT_LABELS[env];
}

export const Login: React.FC = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{ email?: string; password?: string }>({});
    const [onboardingBusy, setOnboardingBusy] = useState(false);
    const [onboardingError, setOnboardingError] = useState<string | null>(null);

    const { login, loading, error, clearError } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const locationError = (location.state as LoginLocationState | null)?.onboardingError;
    const envLabel = environmentLabel(APP_ENV);

    const validateForm = (): boolean => {
        const errors: { email?: string; password?: string } = {};
        if (!validateEmailOrUsername(email)) {
            errors.email = "Email or username is required";
        }
        const passwordValidation = validatePasswordMinLength(password);
        if (!passwordValidation.valid) {
            errors.password = passwordValidation.errors[0];
        }
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        clearError();
        if (!validateForm()) return;
        const result = await login({ email, password });
        if (result.outcome === "authenticated") {
            const nextAccount =
              accountTypeFromRoles(rolesFromClaims(authStorage.getTokenClaims())) ?? 'employee';
            navigate(homePath(nextAccount), { replace: true });
        }
    };

    const handleSignUp = async () => {
        clearError();
        setOnboardingError(null);
        setOnboardingBusy(true);
        try {
            await startHostedOnboarding();
        } catch (err) {
            setOnboardingError(
                err instanceof Error && err.message.trim()
                    ? err.message
                    : 'Could not open sign-up. Please try again.',
            );
            setOnboardingBusy(false);
        }
    };

    return (
        <div className="min-h-screen flex bg-shell-900">
            {/* Left panel — branding */}
            <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                        </svg>
                    </div>
                    <span className="text-white font-bold text-xl">{coreConfig.applicationDisplayName}</span>
                </div>

                <div>
                    <h1 className="text-4xl font-extrabold text-white leading-tight mb-4">
                        Your professional<br />identity, verified.
                    </h1>
                    <p className="text-brand-200 text-lg leading-relaxed mb-8">
                        Store your qualifications, work history and references in one place — and share them instantly with employers.
                    </p>
                    <div className="flex flex-col gap-3">
                        {[
                            { icon: "M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z", text: "Verified qualifications & certifications" },
                            { icon: "M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z", text: "Complete employment history" },
                            { icon: "M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z", text: "Export a professional CV instantly" },
                        ].map(({ icon, text }) => (
                            <div key={text} className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                                    <svg className="w-4 h-4 text-brand-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
                                    </svg>
                                </div>
                                <span className="text-brand-100 text-sm">{text}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <p className="text-brand-400 text-sm">© {new Date().getFullYear()} {coreConfig.applicationDisplayName}</p>
            </div>

            {/* Right panel — form */}
            <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-white">
                <div className="w-full max-w-sm">
                    {/* Mobile logo */}
                    <div className="flex items-center gap-2 mb-8 lg:hidden">
                        <div className="w-8 h-8 bg-brand-600 rounded-lg flex items-center justify-center">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0" />
                            </svg>
                        </div>
                        <span className="font-bold text-gray-900 text-lg">{coreConfig.applicationDisplayName}</span>
                    </div>

                    <h2 className="text-2xl font-extrabold text-gray-900 mb-1">Welcome back</h2>
                    <p className="text-sm text-gray-500 mb-8">Sign in to your account to continue</p>

                    {(error || onboardingError || locationError) && (
                        <Alert
                            variant="danger"
                            onClose={() => {
                                clearError();
                                setOnboardingError(null);
                            }}
                            className="mb-5"
                        >
                            {error || onboardingError || locationError}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <Input
                            label="Email or username"
                            type="text"
                            value={email}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setEmail(e.target.value.toLowerCase())
                            }
                            error={validationErrors.email}
                            autoComplete="username"
                            autoCapitalize="none"
                            autoCorrect="off"
                            spellCheck={false}
                            required
                        />

                        <Input
                            label="Password"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                            error={validationErrors.password}
                            autoComplete="current-password"
                            required
                            rightElement={
                                <button
                                    type="button"
                                    tabIndex={-1}
                                    onClick={() => setShowPassword(v => !v)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                >
                                    {showPassword ? (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                        </svg>
                                    ) : (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                    )}
                                </button>
                            }
                        />

                        <Button type="submit" fullWidth loading={loading} size="lg">
                            Sign in
                        </Button>

                        {envLabel ? (
                            <div className="flex items-center gap-2 pt-1">
                                <div className="h-px flex-1 bg-[#e2e8f0]" />
                                <span className="text-xs font-semibold uppercase tracking-[2px] text-[#94a3b8]">
                                    {envLabel}
                                </span>
                                <div className="h-px flex-1 bg-[#e2e8f0]" />
                            </div>
                        ) : null}
                    </form>

                    <p className="text-sm text-gray-500 mt-6 text-center">
                        Don&apos;t have an account?{' '}
                        <button
                            type="button"
                            disabled={onboardingBusy}
                            onClick={() => void handleSignUp()}
                            className="font-medium text-brand-600 hover:text-brand-700 disabled:opacity-60"
                        >
                            Sign up
                        </button>
                    </p>
                </div>
            </div>
        </div>
    );
};
